import { FrenchContractDataDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ContractFieldsProps } from 'modules/contracts/lib/types'

export interface EmployeeType {
  value: string
  label: string
}

export interface ForfaitJours {
  value: boolean
  label: string
}

const employeeTypes = (): EmployeeType[] => [
  {
    value: 'employee',
    label: i18n.t('contract_versions.forms.contract_extra_information.fr_employee_types.employee'),
  },
  {
    value: 'interim',
    label: i18n.t('contract_versions.forms.contract_extra_information.fr_employee_types.interim'),
  },
  {
    value: 'freelance',
    label: i18n.t('contract_versions.forms.contract_extra_information.fr_employee_types.freelance'),
  },
  {
    value: 'agent_de_maitrise',
    label: i18n.t(
      'contract_versions.forms.contract_extra_information.fr_employee_types.agent_de_maitrise'
    ),
  },
  {
    value: 'apprenti',
    label: i18n.t('contract_versions.forms.contract_extra_information.fr_employee_types.apprenti'),
  },
  {
    value: 'article_36',
    label: i18n.t(
      'contract_versions.forms.contract_extra_information.fr_employee_types.article_36'
    ),
  },
  {
    value: 'cadres',
    label: i18n.t('contract_versions.forms.contract_extra_information.fr_employee_types.cadres'),
  },
  {
    value: 'dirigeant',
    label: i18n.t('contract_versions.forms.contract_extra_information.fr_employee_types.dirigeant'),
  },
  {
    value: 'etam',
    label: i18n.t('contract_versions.forms.contract_extra_information.fr_employee_types.etam'),
  },
  {
    value: 'ouvriers',
    label: i18n.t('contract_versions.forms.contract_extra_information.fr_employee_types.ouvriers'),
  },
  {
    value: 'saisonnier',
    label: i18n.t(
      'contract_versions.forms.contract_extra_information.fr_employee_types.saisonnier'
    ),
  },
  {
    value: 'vrp',
    label: i18n.t('contract_versions.forms.contract_extra_information.fr_employee_types.vrp'),
  },
  {
    value: 'contrat_professionnalisation',
    label: i18n.t(
      'contract_versions.forms.contract_extra_information.fr_employee_types.contrat_professionnalisation'
    ),
  },
  {
    value: 'extra',
    label: i18n.t('contract_versions.forms.contract_extra_information.fr_employee_types.extra'),
  },
  {
    value: 'stagiaire',
    label: i18n.t('contract_versions.forms.contract_extra_information.fr_employee_types.stagiaire'),
  },
]

const forfaitJours = (): ForfaitJours[] => [
  {
    value: true,
    label: i18n.t('common.yes'),
  },
  {
    value: false,
    label: i18n.t('common.no'),
  },
]

export interface FrenchContractData {
  loading: boolean
  contractTypes: ContractFieldsProps[]
  workTypes: ContractFieldsProps[]
  steps: ContractFieldsProps[]
  mutuals: ContractFieldsProps[]
  levels: ContractFieldsProps[]
  professionalCategories: ContractFieldsProps[]
  employeeTypes: EmployeeType[]
  forfaitJours: ForfaitJours[]
}

const useFetchFrenchContractData = (country: string, notHidden?: boolean): FrenchContractData => {
  const { company } = useCurrentSession()
  const skipQuery = !['fr', 'it'].includes(country)
  const { loading: loadingData, data } = useQuery(FrenchContractDataDocument, {
    variables: {
      companyId: company.id,
      notHidden,
    },
    skip: skipQuery,
  })
  const templateData = data?.contracts.contractTemplatesConnection.nodes[0]
  const contractTypes = templateData?.frenchContractType || []
  const workTypes = templateData?.frenchWorkType || []
  const steps = templateData?.frenchStep || []
  const mutuals = templateData?.frenchMutual || []
  const levels = templateData?.frenchLevel || []
  const professionalCategories = templateData?.frenchProfessionalCategory || []

  return {
    loading: loadingData,
    contractTypes,
    workTypes,
    steps,
    mutuals,
    levels,
    professionalCategories,
    employeeTypes: employeeTypes(),
    forfaitJours: forfaitJours(),
  }
}

export default useFetchFrenchContractData
